import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useOnClickOutside } from 'shared/hooks';
import { C, T } from 'shared/helpers';

import Styles from './Select.module.scss';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleDown,
  faAngleDoubleDown,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';

const Select = ({
  selectKey,
  label,
  placeholder,
  stateKey,
  options,
  valueRef,
  setValueRef,
  openRef,
  setOpenRef,
  onChoice,
  disabled,
  nullable,
  onNull,
  style,
  boxStyle,
  optionStyle,
  invertListDirection,
  listStyle,
  id,
}) => {
  const {
    Select,
    Label,
    Box,
    List,
    Open,
    Disabled,
    Scroll,
    Selected,
    ReverseOpen,
  } = Styles;

  const [lookup, setLookup] = useState('');
  const selectRef = useRef();
  /** @type {import('react').Ref<HTMLDivElement>} */
  const dropdownRef = useRef();
  const search = useRef(null);
  const [openState, setOpenState] = useState(false);
  const [lock, setLock] = useState(true);
  const [init, setInit] = useState(false);
  const [value, setValue] = [valueRef, setValueRef];
  const [offset, setOffset] = useState({});
  const [open, setOpen] =
    openRef && setOpenRef ? [openRef, setOpenRef] : [openState, setOpenState];

  useEffect(() => {
    const { current } = dropdownRef;
    if (!current) {
      return;
    }
    if (open) {
      const { innerHeight } = window;
      setTimeout(() => {
        const { y, height } = current.getBoundingClientRect();
        if (y + height > innerHeight) {
          setOffset({ top: -(y + height - innerHeight + 16) });
        } else {
          setOffset({});
        }
      }, 200);
    } else {
      setOffset({});
    }
  }, [open]);
  useEffect(() => {
    if (!value && setDisplay) {
      setDisplay(undefined);
    }
  }, [value]);

  const rLabel = label;

  const [display, setDisplay] = useState('');

  if (options && JSON.stringify(options) !== '[]') {
    while (
      options[0] instanceof Array &&
      !(
        options[0].length === 2 &&
        typeof options[0][0] === 'string' &&
        typeof options[0][1] === 'string'
      )
    ) {
      options = options[0];
    }
    if (
      options instanceof Array &&
      options[0] instanceof Array &&
      options[0][1]
    ) {
      options = options.map((option) => ({
        label: option[1] || option[0],
        value: option[0],
      }));
    } else if (options instanceof Array && typeof options[0] === 'string') {
      options = options.map((option) => ({
        label: option,
        value: option,
      }));
    }
  } else {
    options = [{ label: '', value: '' }];
  }

  disabled =
    typeof disabled === 'function'
      ? disabled({ options, value, label, placeholder })
      : disabled;

  const choose = (label, value, perpetuate = true) => {
    if (perpetuate) {
      setValue(stateKey ? (state) => ({ ...state, [stateKey]: value }) : value);
      setDisplay(label);
      onChoice(value);
      setTimeout(() => {
        setOpen(false);
      }, 200);
    }
  };

  useEffect(() => {
    if (valueRef) {
      setDisplay(T(options.find((x) => x.value === valueRef)).label);
    }
  }, [valueRef, options]);

  useEffect(() => {
    if (!lock && valueRef) {
      setInit(true);
      setLock(true);
      setValue(
        stateKey ? (state) => ({ ...state, [stateKey]: valueRef }) : valueRef,
      );
      setDisplay(T(options.find((x) => x.value === valueRef)).label);
      onChoice(valueRef);
      setTimeout(() => {
        setOpen(false);
      }, 200);
    }
  }, [
    valueRef,
    options,
    stateKey,
    setValue,
    setDisplay,
    onChoice,
    setOpen,
    lock,
    setLock,
    init,
    setInit,
  ]);

  const firstOptionRef = useRef();

  useOnClickOutside(selectRef, () => setOpen(false));

  return (
    <div
      ref={selectRef}
      style={{ ...style }}
      className={C(Select, [disabled, Disabled])}
    >
      <div className={Label} style={{ display: label ? 'block' : 'none' }}>
        {label}
      </div>
      <div
        onClick={
          !disabled
            ? () =>
                setOpen((o) => !o) || (search.current && search.current.focus())
            : null
        }
        style={{
          ...boxStyle,
          color: !boxStyle.color && !value ? '#999' : undefined,
        }}
        id={id}
        data-testid={id}
        className={C(Box, [open, Open])}
      >
        <p>{display || placeholder}</p>
        {nullable && value && (
          <span
            id='select_clear_btn'
            data-testid={`${id}-clear`}
            onClick={
              !disabled &&
              ((e) =>
                e.stopPropagation() || choose(undefined, undefined) || onNull())
            }
            style={{ right: '30px' }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </span>
        )}
        <span>
          <FontAwesomeIcon
            icon={open && !disabled ? faAngleDoubleDown : faAngleDown}
          />
        </span>
      </div>
      {!disabled && options && options.length && options[0].value && (
        <div
          ref={dropdownRef}
          className={C(
            List,
            [open && !disabled, Open],
            [invertListDirection, ReverseOpen],
          )}
          id={id ? `${id}_options` : undefined}
          style={open ? { ...listStyle, ...offset } : {}}
        >
          {open && (
            <>
              <div>
                <input
                  autoFocus
                  value={lookup}
                  onChange={({ target }) => setLookup(target.value)}
                  ref={search}
                  onKeyDown={(e) => {
                    const { key } = e;
                    if (key === 'ArrowDown') {
                      if (firstOptionRef.current) {
                        firstOptionRef.current.focus();
                        e.preventDefault();
                      }
                    }
                  }}
                />
              </div>
              <div className={Scroll}>
                {options
                  .filter(
                    ({ label }) =>
                      !lookup ||
                      (label || '')
                        .toLocaleLowerCase()
                        .includes((lookup || '').toLocaleLowerCase()),
                  )
                  .map(({ label, value }, index) => (
                    <div
                      tabIndex={0}
                      ref={index === 0 ? firstOptionRef : undefined}
                      key={`${selectKey}_${(rLabel || '').normalize(
                        'NFD',
                      )}_${label}_${value}_${++index}`}
                      className={C(
                        'option',
                        value === valueRef ? Selected : null,
                      )}
                      style={{
                        display: 'flex',
                      }}
                      onClick={() => choose(label, value)}
                      onKeyDown={({ keyCode, key, currentTarget }) => {
                        if (keyCode === 13 || key === 'Enter') {
                          choose(label, value);
                        }
                        if (key === 'ArrowDown') {
                          if (currentTarget.nextSibling) {
                            currentTarget.nextSibling.focus();
                          }
                        }
                        if (key === 'ArrowUp') {
                          if (currentTarget.previousSibling) {
                            currentTarget.previousSibling.focus();
                          } else if (search.current) {
                            search.current.focus();
                          }
                        }
                      }}
                    >
                      <p style={{ ...optionStyle }}>{label}</p>
                    </div>
                  ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

Select.defaultProps = {
  selectKey: '',
  label: '',
  placeholder: '',
  stateKey: '',
  valueRef: null,
  setValueRef: null,
  openRef: null,
  setOpenRef: null,
  onChoice: () => {},
  options: [],
  disabled: false,
  style: {},
  boxStyle: {},
  optionStyle: {},
  listStyle: {},
  invertListDirection: false,
  nullable: false,
  onNull: () => {},
  id: undefined,
};

Select.propTypes = {
  style: PropTypes.object,
  selectKey: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  stateKey: PropTypes.string,
  options: PropTypes.any,
  valueRef: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setValueRef: PropTypes.func,
  openRef: PropTypes.bool,
  setOpenRef: PropTypes.func,
  onChoice: PropTypes.func,
  disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  boxStyle: PropTypes.object,
  optionStyle: PropTypes.object,
  listStyle: PropTypes.object,
  invertListDirection: PropTypes.bool,
  nullable: PropTypes.bool,
  onNull: PropTypes.func,
  id: PropTypes.string,
};

export default Select;
